import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import FastImageUploader from "./components/FastImageUploader";
import PixelBrickCanvas from "./components/PixelBrickCanvas";
import ConversionResults from "./components/ConversionResults";
import LanguageSelector from "./components/LanguageSelector";
import SectionRule from "./components/SectionRule";
import { Button } from "./@/components/ui/button";
import {
  PALETTE_MODES,
  remapToCanonicalIndices,
  type PaletteMode,
} from "./constants/legoColors";
import { createPixelationWorker } from "./workers/pixelation";
import { pixelateImageData } from "./workers/pixelationLogic";
import { physicalSize } from "./utils/physicalSize";
import { EXAMPLE_IMAGES } from "./data/exampleImages";
import { buildFromObjText, colorIndexByName, DEFAULT_MODEL_COLOR_NAME } from "./utils/modelToLayers";
import type { LayerData } from "./utils/brickOptimizer";
import { ModelSettings, ModelStage } from "./components/ModelWorkbench";
import ModelResultStrip from "./components/ModelResultStrip";
import {
  clearShareHash,
  parseShareState,
  writeShareHash,
} from "./utils/shareState";

interface PixelData {
  pixels: number[][];
  width: number;
  height: number;
}

interface Model3d {
  layers: LayerData[];
  width: number;
  height: number;
  name: string;
}

function App() {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [pixelData, setPixelData] = useState<PixelData | null>(null);
  const [model3d, setModel3d] = useState<Model3d | null>(null);
  const [inputMode, setInputMode] = useState<"model" | "image">("model");
  const [objText, setObjText] = useState<string | null>(null);
  const [modelName, setModelName] = useState<string | null>(null);
  const [resolution, setResolution] = useState(24);
  const [modelColor, setModelColor] = useState(() =>
    colorIndexByName(DEFAULT_MODEL_COLOR_NAME),
  );
  const [appliedResolution, setAppliedResolution] = useState<number | null>(
    null,
  );
  const [appliedModelColor, setAppliedModelColor] = useState<number | null>(
    null,
  );
  const [pixelSize, setPixelSize] = useState(20);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(false);
  // Explicit transform: the conversion runs only when the user presses the
  // Transform button (bumped nonce), not automatically on upload/slider moves
  const [transformNonce, setTransformNonce] = useState(0);
  const [appliedPixelSize, setAppliedPixelSize] = useState<number | null>(null);
  const [paletteMode, setPaletteMode] = useState<PaletteMode>("full");
  const [appliedPaletteMode, setAppliedPaletteMode] =
    useState<PaletteMode | null>(null);
  const [dither, setDither] = useState(false);
  const [appliedDither, setAppliedDither] = useState<boolean | null>(null);
  const [mergeRareColors, setMergeRareColors] = useState(false);
  const [appliedMergeRareColors, setAppliedMergeRareColors] = useState<
    boolean | null
  >(null);
  const [depth3d, setDepth3d] = useState(false);
  const [appliedDepth3d, setAppliedDepth3d] = useState<boolean | null>(null);
  const pixelSizeRef = useRef(pixelSize);
  const paletteModeRef = useRef(paletteMode);
  const ditherRef = useRef(dither);
  const mergeRareColorsRef = useRef(mergeRareColors);
  const depth3dRef = useRef(depth3d);
  const workerRef = useRef<Worker | null>(null);
  const workerUrlRef = useRef<string | null>(null);
  // Where the current image came from — only example images are shareable
  const exampleIdRef = useRef<string | null>(null);

  // Parsed once at first render so a shared link can pre-seed the uploader
  const [sharedState] = useState(() => parseShareState(window.location.hash));
  const sharedExample = sharedState?.exampleId
    ? EXAMPLE_IMAGES.find((image) => image.id === sharedState.exampleId)
    : undefined;

  useEffect(() => {
    pixelSizeRef.current = pixelSize;
  }, [pixelSize]);

  useEffect(() => {
    paletteModeRef.current = paletteMode;
  }, [paletteMode]);

  useEffect(() => {
    ditherRef.current = dither;
  }, [dither]);

  useEffect(() => {
    mergeRareColorsRef.current = mergeRareColors;
  }, [mergeRareColors]);

  useEffect(() => {
    depth3dRef.current = depth3d;
  }, [depth3d]);

  // Restore a shared link: apply its settings, load the example image and
  // run the conversion — the one legitimate auto-transform, since opening
  // the link IS the explicit intent
  useEffect(() => {
    if (!sharedState) return;

    // A shared image link belongs to the image flow
    if (sharedExample) setInputMode("image");

    setPixelSize(sharedState.pixelSize);
    pixelSizeRef.current = sharedState.pixelSize;
    setPaletteMode(sharedState.paletteMode);
    paletteModeRef.current = sharedState.paletteMode;
    setDither(sharedState.dither);
    ditherRef.current = sharedState.dither;
    setMergeRareColors(sharedState.mergeRareColors);
    mergeRareColorsRef.current = sharedState.mergeRareColors;
    setDepth3d(sharedState.depth3d ?? false);
    depth3dRef.current = sharedState.depth3d ?? false;

    if (sharedExample) {
      exampleIdRef.current = sharedExample.id;
      setOriginalImage(
        `${window.location.origin}${sharedExample.path}`,
      );
      setTransformNonce(1);
    }
  }, [sharedState, sharedExample]);

  const handleImageUpload = useCallback(
    (url: string, exampleId?: string) => {
      // The previous share state no longer describes the app
      clearShareHash();
      exampleIdRef.current = exampleId ?? null;
      setOriginalImage(url);
      setModel3d(null); // an image upload replaces any 3D-model build
      setObjText(null);
      setModelName(null);
      setAppliedResolution(null);
      setAppliedModelColor(null);
      setPixelData(null);
      setAppliedPixelSize(null);
      setAppliedPaletteMode(null);
      setAppliedDither(null);
      setAppliedMergeRareColors(null);
      setAppliedDepth3d(null);
      setTransformNonce(0);
    },
    [],
  );

  // Storing an OBJ only records the input; the pipeline runs on Build
  const handleObjFile = useCallback((text: string, name: string) => {
    clearShareHash();
    exampleIdRef.current = null;
    setOriginalImage(null);
    setPixelData(null);
    setAppliedPixelSize(null);
    setAppliedPaletteMode(null);
    setAppliedDither(null);
    setAppliedMergeRareColors(null);
    setAppliedDepth3d(null);
    setTransformNonce(0);
    setModel3d(null);
    setAppliedResolution(null);
    setAppliedModelColor(null);
    setObjText(text);
    setModelName(name);
  }, []);

  // Build/Rebuild: run the pipeline from the stored input + current settings
  const handleModelBuild = useCallback(() => {
    if (!objText) return;
    setIsProcessing(true);
    setProcessingError(false);

    window.setTimeout(() => {
      try {
        const built = buildFromObjText(objText, {
          resolution,
          colorIndex: modelColor,
        });
        setModel3d({
          layers: built.layers,
          width: built.width,
          height: built.height,
          name: modelName ?? "model",
        });
        setAppliedResolution(resolution);
        setAppliedModelColor(modelColor);
      } catch {
        setModel3d(null);
        setProcessingError(true);
      } finally {
        setIsProcessing(false);
      }
    }, 30);
  }, [objText, resolution, modelColor, modelName]);

  useEffect(() => {
    if (!originalImage || transformNonce === 0) return;

    const effectivePixelSize = pixelSizeRef.current;
    const effectiveMode = paletteModeRef.current;
    const effectiveDither = ditherRef.current;
    const effectiveMergeRareColors = mergeRareColorsRef.current;
    const effectiveDepth3d = depth3dRef.current;
    const palette = PALETTE_MODES[effectiveMode];
    let cancelled = false;
    setIsProcessing(true);
    setProcessingError(false);

    const cleanupWorker = () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      if (workerUrlRef.current) {
        URL.revokeObjectURL(workerUrlRef.current);
        workerUrlRef.current = null;
      }
    };

    const finish = (data: PixelData) => {
      if (!cancelled) {
        setPixelData({
          ...data,
          pixels: remapToCanonicalIndices(data.pixels, palette),
        });
        setAppliedPixelSize(effectivePixelSize);
        setAppliedPaletteMode(effectiveMode);
        setAppliedDither(effectiveDither);
        setAppliedMergeRareColors(effectiveMergeRareColors);
        setAppliedDepth3d(effectiveDepth3d);
        setIsProcessing(false);
        // The hash mirrors the applied state, so the URL can be copied
        // straight out of the address bar to share this conversion
        writeShareHash({
          pixelSize: effectivePixelSize,
          paletteMode: effectiveMode,
          dither: effectiveDither,
          mergeRareColors: effectiveMergeRareColors,
          depth3d: effectiveDepth3d,
          exampleId: exampleIdRef.current ?? undefined,
        });
      }
      cleanupWorker();
    };

    const fail = () => {
      if (!cancelled) {
        setProcessingError(true);
        setIsProcessing(false);
      }
      cleanupWorker();
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;

      let imageData: ImageData;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no 2d context");
        ctx.drawImage(img, 0, 0);
        imageData = ctx.getImageData(0, 0, img.width, img.height);
      } catch {
        fail();
        return;
      }

      cleanupWorker();

      // Prefer the Web Worker; fall back to the main thread when Workers
      // are unavailable or blocked (e.g. strict CSP)
      let worker: Worker | null = null;
      let url: string | null = null;
      try {
        const created = createPixelationWorker();
        worker = created.worker;
        url = created.url;
      } catch {
        worker = null;
      }

      if (!worker) {
        window.setTimeout(() => {
          if (cancelled) return;
          try {
            finish(
              pixelateImageData(
                new Uint8ClampedArray(imageData.data),
                img.width,
                img.height,
                effectivePixelSize,
                palette,
                { dither: effectiveDither, mergeRareColors: effectiveMergeRareColors },
              ),
            );
          } catch {
            fail();
          }
        }, 0);
        return;
      }

      workerRef.current = worker;
      workerUrlRef.current = url;

      worker.onmessage = (e) => {
        finish(e.data);
      };

      worker.onerror = () => {
        fail();
      };

      worker.postMessage(
        {
          imageData: imageData.data.buffer,
          width: img.width,
          height: img.height,
          pixelSize: effectivePixelSize,
          legoColors: palette,
          dither: effectiveDither,
          mergeRareColors: effectiveMergeRareColors,
        },
        [imageData.data.buffer],
      );
    };
    img.onerror = fail;
    img.src = originalImage;

    return () => {
      cancelled = true;
      cleanupWorker();
    };
  }, [originalImage, transformNonce]);

  const uniqueColors = useMemo(
    () => (pixelData ? new Set(pixelData.pixels.flat()).size : 0),
    [pixelData],
  );

  const size = pixelData
    ? physicalSize(pixelData.width, pixelData.height)
    : null;

  // The bench shows a settings column only when there are settings
  const hasSettings = inputMode === "model" || originalImage !== null;

  return (
    <div className="min-h-screen bg-workshop font-sans text-ink antialiased flex flex-col">
      <header className="sticky top-0 z-30 bg-surface/95 backdrop-blur-md border-b-2 border-ink/90">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="title-accent-bar w-1.5 h-7 shrink-0"
              aria-hidden
            />
            <span className="font-display font-extrabold text-xl tracking-tight text-ink truncate">
              bricksup
            </span>
          </div>
          <LanguageSelector />
        </div>
        <div className="hero-strip h-1 w-full" aria-hidden />
      </header>

      <main className="max-w-5xl mx-auto px-5 pb-24 flex flex-col">
        {/* WORKBENCH BAR — the tool opens on its work, not on a pitch */}
        <div className="pt-6 sm:pt-8 pb-5 border-b border-ink/10">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <h1 className="max-w-none font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-ink leading-tight">
              {t("app.title")}
            </h1>
            <div
              className="flex shrink-0 items-center gap-1 rounded-full border-[1.5px] border-ink bg-surface p-1 shadow-hard-sm"
              role="group"
              aria-label={t("model.modeLabel")}
            >
              <button
                type="button"
                aria-pressed={inputMode === "model"}
                onClick={() => setInputMode("model")}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick ${
                  inputMode === "model"
                    ? "bg-brick text-surface"
                    : "text-ink-muted hover:text-brick"
                }`}
              >
                {t("model.mode3d")}
              </button>
              <button
                type="button"
                aria-pressed={inputMode === "image"}
                onClick={() => setInputMode("image")}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-brick ${
                  inputMode === "image"
                    ? "bg-brick text-surface"
                    : "text-ink-muted hover:text-brick"
                }`}
              >
                {t("model.modeImage")}
              </button>
            </div>
          </div>
          <p className="mt-2 text-sm text-ink-muted leading-relaxed max-w-md">
            {t("app.subtitle")}
          </p>
        </div>

        {/* THE BENCH — the stage and the settings that change it, side by
            side on wide screens so the build button sits beside its build */}
        <div
          className={`mt-8 grid gap-x-6 gap-y-8 ${
            hasSettings ? "lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start" : ""
          }`}
        >
          {/* The stage: the live model, the mosaic, or the drop target */}
          <div>
            <section
              aria-label={
                inputMode === "model" ? t("model.mode3d") : t("uploader.title")
              }
            >
            {inputMode === "model" ? (
              <ModelStage
                objText={objText}
                modelName={modelName}
                resolution={resolution}
                colorIndex={modelColor}
                isProcessing={isProcessing}
                hasBuild={model3d !== null}
                pending={
                  model3d !== null &&
                  (resolution !== appliedResolution ||
                    modelColor !== appliedModelColor)
                }
                onObjFile={handleObjFile}
                onResolutionChange={setResolution}
                onColorChange={setModelColor}
                onBuild={handleModelBuild}
              />
            ) : pixelData ? (
              <div className="panel p-3 sm:p-4">
                <div className="flex flex-wrap items-center gap-2 mb-3 px-1">
                  <span className="spec-chip text-ink">
                    {pixelData.width} × {pixelData.height}
                  </span>
                  <span className="spec-chip text-ink">
                    {uniqueColors} {t("converter.colors")}
                  </span>
                  {size && (
                    <span className="spec-chip text-ink">
                      ~{size.widthCm} × {size.heightCm} cm ·{" "}
                      {t("converter.baseplate", { count: size.baseplates })}
                    </span>
                  )}
                </div>
                <PixelBrickCanvas
                  pixels={pixelData.pixels}
                  width={pixelData.width}
                  height={pixelData.height}
                />
              </div>
            ) : (
              <FastImageUploader
                onImageUpload={handleImageUpload}
                initialImageUrl={originalImage ?? undefined}
              />
            )}
          </section>

          {/* Image flow: swap the source without leaving the stage */}
          {inputMode === "image" && pixelData && (
            <div className="mt-4">
              <FastImageUploader
                compact
                onImageUpload={handleImageUpload}
                initialImageUrl={originalImage ?? undefined}
              />
            </div>
          )}
          </div>

          {/* The settings — the build controls beside the thing they build */}
          {inputMode === "model" ? (
          <section aria-label={t("model.buildSettings")}>
            <SectionRule title={t("model.buildSettings")} />
            <ModelSettings
                objText={objText}
                modelName={modelName}
                resolution={resolution}
                colorIndex={modelColor}
                isProcessing={isProcessing}
                pending={
                  model3d !== null &&
                  (resolution !== appliedResolution ||
                    modelColor !== appliedModelColor)
                }
                onObjFile={handleObjFile}
                onResolutionChange={setResolution}
              onColorChange={setModelColor}
              onBuild={handleModelBuild}
            />
          </section>
          ) : originalImage ? (
          <section aria-label={t("controls.tuneTitle")}>
            <SectionRule title={t("controls.tuneTitle")} />
            <div className="panel panel-hover p-6">
              {/* Resolution */}
              <div className="flex items-baseline justify-between gap-4 mb-5">
                <label htmlFor="pixel-size-slider" className="eyebrow">
                  {t("controls.pixelSize")}
                </label>
                <span
                  className="text-4xl font-extrabold text-brick tabular-nums leading-none"
                  aria-hidden
                >
                  {pixelSize}
                  <span className="text-ink-muted text-base font-semibold ml-1">
                    px
                  </span>
                </span>
              </div>
              <input
                id="pixel-size-slider"
                type="range"
                min="8"
                max="32"
                step="2"
                value={pixelSize}
                onChange={(e) => setPixelSize(Number(e.target.value))}
                className="w-full h-2 rounded-full bg-canvas accent-brick cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
              />
              <div className="flex justify-between mt-2 text-xs font-semibold text-ink-muted">
                <span>{t("controls.fasterHint")}</span>
                <span>{t("controls.slowerHint")}</span>
              </div>

              {/* Color processing */}
              <div className="mt-8 pt-6 border-t border-ink/10">
                <span className="eyebrow text-brick" id="color-processing-label">
                  {t("controls.colorProcessing")}
                </span>

                <div className="mt-4">
                  <span className="eyebrow text-ink-muted" id="palette-mode-label">
                    {t("controls.palette")}
                  </span>
                  <div
                    className="mt-2 flex flex-wrap gap-2"
                    role="group"
                    aria-labelledby="palette-mode-label"
                  >
                    {(Object.keys(PALETTE_MODES) as PaletteMode[]).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        aria-pressed={paletteMode === mode}
                        onClick={() => setPaletteMode(mode)}
                        className={`rounded-full border-[1.5px] px-3 py-1 text-sm font-semibold shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press ${
                          paletteMode === mode
                            ? "border-brick bg-brick text-surface"
                            : "border-ink bg-surface text-ink hover:text-brick hover:border-brick"
                        }`}
                      >
                        {t(
                          `controls.palette${mode.charAt(0).toUpperCase() + mode.slice(1)}`,
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <span className="eyebrow text-ink-muted" id="dithering-label">
                    {t("controls.dithering")}
                  </span>
                  <div className="mt-2" role="group" aria-labelledby="dithering-label">
                    <button
                      type="button"
                      aria-pressed={dither}
                      onClick={() => setDither((v) => !v)}
                      className={`rounded-full border-[1.5px] px-3 py-1 text-sm font-semibold shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press ${
                        dither
                          ? "border-brick bg-brick text-surface"
                          : "border-ink bg-surface text-ink hover:text-brick hover:border-brick"
                      }`}
                    >
                      {dither ? t("controls.ditheringOn") : t("controls.ditheringOff")}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-ink-muted leading-relaxed">
                    {t("controls.ditheringHint")}
                  </p>
                </div>

                <div className="mt-4">
                  <span className="eyebrow text-ink-muted" id="merge-rare-label">
                    {t("controls.mergeRareColors")}
                  </span>
                  <div className="mt-2" role="group" aria-labelledby="merge-rare-label">
                    <button
                      type="button"
                      aria-pressed={mergeRareColors}
                      onClick={() => setMergeRareColors((v) => !v)}
                      className={`rounded-full border-[1.5px] px-3 py-1 text-sm font-semibold shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press ${
                        mergeRareColors
                          ? "border-brick bg-brick text-surface"
                          : "border-ink bg-surface text-ink hover:text-brick hover:border-brick"
                      }`}
                    >
                      {mergeRareColors ? t("controls.ditheringOn") : t("controls.ditheringOff")}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-ink-muted leading-relaxed">
                    {t("controls.mergeRareHint")}
                  </p>
                </div>

                <div className="mt-4">
                  <span className="eyebrow text-ink-muted" id="depth-3d-label">
                    {t("controls.depth3d")}
                  </span>
                  <div className="mt-2" role="group" aria-labelledby="depth-3d-label">
                    <button
                      type="button"
                      aria-pressed={depth3d}
                      onClick={() => setDepth3d((v) => !v)}
                      className={`rounded-full border-[1.5px] px-3 py-1 text-sm font-semibold shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press ${
                        depth3d
                          ? "border-brick bg-brick text-surface"
                          : "border-ink bg-surface text-ink hover:text-brick hover:border-brick"
                      }`}
                    >
                      {depth3d ? t("controls.ditheringOn") : t("controls.ditheringOff")}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-ink-muted leading-relaxed">
                    {t("controls.depth3dHint")}
                  </p>
                </div>
              </div>

              {/* Run */}
              <Button
                onClick={() => setTransformNonce((n) => n + 1)}
                disabled={isProcessing}
                className="btn-press w-full mt-8"
              >
                {isProcessing
                  ? t("common.processing")
                  : pixelData &&
                      (pixelSize !== appliedPixelSize ||
                        paletteMode !== appliedPaletteMode ||
                        dither !== appliedDither ||
                        mergeRareColors !== appliedMergeRareColors ||
                        depth3d !== appliedDepth3d)
                    ? t("controls.updateMosaic")
                    : t("controls.transform")}
              </Button>

              {isProcessing && (
                <div
                  className="flex items-center gap-3 text-ink-muted mt-4"
                  role="status"
                >
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-canvas border-t-brick [prefers-reduced-motion:reduce]:animate-none" />
                  <span className="text-sm font-semibold">
                    {t("common.processing")}
                  </span>
                </div>
              )}
              {processingError && (
                <p
                  className="mt-4 text-sm font-semibold text-brick border-l-4 border-brick pl-3"
                  role="alert"
                >
                  {t("errors.processingFailed")}
                </p>
              )}
            </div>
          </section>
          ) : null}
        </div>

        {/* The build's summary, then the results it opens */}
        {model3d && <ModelResultStrip model={model3d} />}

        {/* Results: model flow (3D build) or image flow (TOC + quality) */}
        {model3d && (
          <div className="mt-14">
            <ConversionResults model={model3d} />
          </div>
        )}

        {/* Results: TOC + quality, instructions, layout, BOM */}
        {pixelData && (
          <div className="mt-14">
            <ConversionResults
              pixelData={pixelData}
              pixelSize={pixelSize}
              depth3d={appliedDepth3d === true}
            />
          </div>
        )}
      </main>

      <footer className="mt-auto border-t-2 border-ink/90 bg-surface">
        <div className="max-w-5xl mx-auto px-5 py-6 flex items-center gap-4">
          <div className="studs-row w-24 opacity-60" aria-hidden />
          <p className="text-sm font-semibold text-ink-muted">
            {t("app.footerTagline")}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
